import React, { useEffect } from "react";
import { Col, Row } from "react-bootstrap";
import PropTypes from 'prop-types';
import { Info as InfoIcon } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { customValidators, object, string } from '../../../Helpers/Validators/customValidators';
import { TextInput, RadioInput, AutocompleteInput, SelectInput, StyledRadioInput } from "../../../Helpers/Inputs";
import { NumberFormatDecimal, InputMaskPhone, InputMaskZip } from '../../../Helpers/Formatters';
import Occupations from '../Helpers/Occupations';
import * as States from '../../../Helpers/Constants/States';
import StatesWithDisabled from '../../../Helpers/Lists/StatesWithDisabled';
import LicenseReasons from '../Helpers/LicenseReasons';
import Step3Icon from '../icons/step_3.svg?react';
import IconHolder from "../../../Helpers/IconHolder";
import { SESSION_STORAGE_ANALYSIS_KEY, getSessionStorageItem } from "../../../../sessionStorage";

export default function IdentityVerification(props) {

  const { trigger, register, setValue } = props;
  const handleValueChange = (field, value) => {
    props.onFieldChange(field, value);
    trigger(field);
  };

  const handleChangeState = (field, value, driverLicenseNumber) => {
    props.onFieldChange(field, value);
    trigger(field);
    if (driverLicenseNumber !== "") {
      trigger('driverLicenseNumber');
    }
  };

  useEffect(() => {
    register('state');
    setValue('state', props.basicInfo.state);
  }, [register]);

  useEffect(() => {
    if (props.formState.annualSalary === null || props.formState.annualSalary === "") {
      const analysisInfo = getSessionStorageItem(SESSION_STORAGE_ANALYSIS_KEY);
      props.onFieldChange('annualSalary', analysisInfo && analysisInfo.annualIncome);
    }
  }, []);

  const occupations = Occupations();
  const licenseReasons = LicenseReasons();

  const label = (
    <span>Designate another person the right to receive notice of an impending lapse or termination of the policy applied for in the event of nonpayment of premium?
      <Tooltip
        title="This is to ensure that your life insurance policy doesn’t lapse unintentionally."
        placement="top"
        enterTouchDelay={0}
        slotProps={{
          tooltip: {
            className: 'light-tooltip',
            sx: { fontSize: '14px' }
          },
        }}><InfoIcon className="ms-2" /></Tooltip>
    </span>);

  return (
    <React.Fragment>
      <Row className="justify-content-center">
        <IconHolder icon={Step3Icon} viewBox="0 0 25 25" />
      </Row>
      <h4 className="headerTitle">Verifying Your Identification</h4>
      <h5 className="headerSubtitle">Your privacy and security are our first priority. This information is used solely to secure your best rate for coverage that you can buy online.</h5>

      <div className="mb-3">
        <RadioInput
          name="employmentStatus"
          id="employmentStatus"
          label="What is your current employment status?"
          value={props.formState.employmentStatus}
          radios={[
            { value: "Employed", label: "Employed" },
          ]}
          onChange={e => handleValueChange('employmentStatus', e.target.value)}
          hideError
        />

        {props.formState.employmentStatus === "Employed" &&
          <div className="ms-4">
            <div className="mb-3">
              <TextInput
                name="annualSalary"
                id="annualSalary"
                label="Annual Salary"
                value={props.formState.annualSalary}
                onChange={e => props.onFieldChange('annualSalary', e.target.value)}
                InputProps={{
                  inputComponent: NumberFormatDecimal,
                }}
              />
            </div>

            <div className="mb-3">
              <AutocompleteInput
                name="occupation"
                id="occupation"
                value={occupations.find(o => o.value === props.formState.occupation)}
                onChange={(value) => handleValueChange('occupation', value)}
                label="Occupation"
                selectOptions={occupations}
              />
            </div>
          </div>
        }

        <RadioInput
          name="employmentStatus"
          id="employmentStatus"
          label=""
          value={props.formState.employmentStatus}
          radios={[
            { value: "Unemployed", label: "Unemployed" },
            { value: "Disabled", label: "Disabled" },
            { value: "Student", label: "Student" },
            { value: "Retired", label: "Retired" },
            { value: "Stay-at-Home Person", label: "Stay-at-Home Person" },
          ]}
          onChange={e => handleValueChange('employmentStatus', e.target.value)}
          hideLabel
        />
      </div>

      <div className="mb-3">
        <StyledRadioInput
          name="validDriverLicense"
          id="validDriverLicense"
          label="Do you have a valid driver’s license?"
          value={props.formState.validDriverLicense}
          options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
          onChange={e => handleValueChange('validDriverLicense', e)}
        />

        {props.formState.validDriverLicense === "true" &&
          <div>
            <div className="mb-3">
              <SelectInput
                name="driverLicenceState"
                id="driverLicenceState"
                value={props.formState.driverLicenceState}
                onChange={(e) => handleChangeState('driverLicenceState', e.target.value, props.formState.driverLicenseNumber)}
                label="Driver's License State"
                selectOptions={StatesWithDisabled(["NY"])}
              />
            </div>

            <div className="mb-3">
              <TextInput
                name="driverLicenseNumber"
                id="driverLicenseNumber"
                label="Driver's License Number"
                value={props.formState.driverLicenseNumber}
                onChange={e => handleValueChange('driverLicenseNumber', e.target.value)}
                disabled={props.formState.driverLicenceState === ""}
              />
            </div>

            <div className="mb-3">
              <TextInput
                name="driverLicenseName"
                id="driverLicenseName"
                label="Name on Driver's License"
                value={props.formState.driverLicenseName}
                onChange={e => props.onFieldChange('driverLicenseName', e.target.value)}
                disabled={props.formState.driverLicenceState === ""}
              />
            </div>

          </div>
        }

        {props.formState.validDriverLicense === "false" &&
          <div className="mb-3">
            <SelectInput
              id="noDrivingReason"
              name="noDrivingReason"
              value={props.formState.noDrivingReason}
              onChange={e => handleValueChange('noDrivingReason', e.target.value)}
              label="Choose a Reason"
              selectOptions={licenseReasons}
            />
          </div>
        }

      </div>

      {isSecondaryAddresseeState(props.basicInfo.state) && (
        <React.Fragment>
          <div className="mb-3">
            <StyledRadioInput
              name="includeSecondaryAdressee"
              id="includeSecondaryAdressee"
              label={label}
              value={props.formState.includeSecondaryAdressee}
              options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
              onChange={e => handleValueChange('includeSecondaryAdressee', e)}
            />
          </div>

          {props.formState.includeSecondaryAdressee === "true" &&
            <React.Fragment>
              <div className="mb-3">
                <TextInput
                  name="secondaryAddresseeFirstName"
                  id="secondaryAddresseeFirstName"
                  label="Designee First Name"
                  value={props.formState.secondaryAddresseeFirstName}
                  onChange={(e) => props.onFieldChange('secondaryAddresseeFirstName', e.target.value)}
                />
              </div>

              <div className="mb-3">
                <TextInput
                  name="secondaryAddresseeMiddleName"
                  id="secondaryAddresseeMiddleName"
                  label="Designee Middle Name"
                  value={props.formState.secondaryAddresseeMiddleName}
                  onChange={(e) => props.onFieldChange('secondaryAddresseeMiddleName', e.target.value)}
                />
              </div>

              <div className="mb-3">
                <TextInput
                  name="secondaryAddresseeLastName"
                  id="secondaryAddresseeLastName"
                  label="Designee Last Name"
                  value={props.formState.secondaryAddresseeLastName}
                  onChange={(e) => props.onFieldChange('secondaryAddresseeLastName', e.target.value)}
                />
              </div>

              <div className="mb-3">
                <SelectInput
                  name="secondaryAddresseePhoneNumberType"
                  id="secondaryAddresseePhoneNumberType"
                  label="Designee Phone Number Type"
                  value={props.formState.secondaryAddresseePhoneNumberType}
                  selectOptions={[{ value: "home", label: "Home" }, { value: "cell", label: "Cell" }, { value: "work", label: "Work" }]}
                  onChange={e => handleValueChange('secondaryAddresseePhoneNumberType', e.target.value)}
                />
              </div>

              <div className="mb-3">
                <TextInput
                  name="secondaryAddresseePhoneNumber"
                  id="secondaryAddresseePhoneNumber"
                  label="Designee Phone Number"
                  value={props.formState.secondaryAddresseePhoneNumber}
                  onChange={(e) => handleValueChange('secondaryAddresseePhoneNumber', e.target.value)}
                  InputProps={{
                    inputComponent: InputMaskPhone,
                  }}
                />
              </div>

              <div className="mb-3">
                <TextInput
                  name="secondaryAddresseeAddress"
                  id="secondaryAddresseeAddress"
                  label="Designee Address"
                  value={props.formState.secondaryAddresseeAddress}
                  onChange={e => props.onFieldChange('secondaryAddresseeAddress', e.target.value)}
                />
              </div>

              <div className="mb-3">
                <TextInput
                  name="secondaryAddresseeCity"
                  id="secondaryAddresseeCity"
                  label="Designee City"
                  value={props.formState.secondaryAddresseeCity}
                  onChange={e => props.onFieldChange('secondaryAddresseeCity', e.target.value)}
                />
              </div>

              <Row>
                <Col>
                  <div className="mb-3">
                    <SelectInput
                      name="secondaryAddresseeState"
                      id="secondaryAddresseeState"
                      label="Designee State"
                      value={props.formState.secondaryAddresseeState}
                      onChange={e => handleValueChange('secondaryAddresseeState', e.target.value)}
                      selectOptions={StatesWithDisabled(["NY"])}
                    />
                  </div>
                </Col>
                <Col>
                  <div className="mb-3">
                    <TextInput
                      name="secondaryAddresseeZip"
                      id="secondaryAddresseeZip"
                      label="Designee Zip"
                      value={props.formState.secondaryAddresseeZip}
                      onChange={e => props.onFieldChange('secondaryAddresseeZip', e.target.value)}
                      InputProps={{
                        inputComponent: InputMaskZip,
                      }}
                    />
                  </div>
                </Col>
              </Row>
            </React.Fragment>
          }

        </React.Fragment>
      )}
    </React.Fragment>
  );
}

const isSecondaryAddresseeState = (state) => {
  if (state === States.CONNECTICUT ||
    state === States.IDAHO ||
    state === States.MAINE ||
    state === States.MONTANA ||
    state === States.RHODE_ISLAND ||
    state === States.UTAH ||
    state === States.VERMONT ||
    state === States.CALIFORNIA ||
    state === States.FLORIDA
  ) {
    return true;
  }
  return false;
};

IdentityVerification.ValidationSchema = object().shape({
  employmentStatus: string().required('This field is required.'),
  annualSalary: string().when('employmentStatus', {
    is: "Employed",
    then: (schema) => schema.required('Annual Salary is required.'),
    otherwise: (schema) => schema.notRequired()
  }),
  occupation: string().when('employmentStatus', {
    is: "Employed",
    then: (schema) => schema.required('Occupation is required.'),
    otherwise: (schema) => schema.notRequired()
  }),
  validDriverLicense: string().required('Must provide an answer.'),
  noDrivingReason: string().when('validDriverLicense', {
    is: "false",
    then: (schema) => schema.required('Please select an option.'),
    otherwise: (schema) => schema.notRequired()
  }),
  driverLicenceState: string().when('validDriverLicense', {
    is: "true",
    then: (schema) => schema.required("Driver's License State is required."),
    otherwise: (schema) => schema.notRequired()
  }),
  driverLicenseNumber: string().when('validDriverLicense', {
    is: "true",
    then: (schema) => schema.when("driverLicenceState", (driverLicenceState) => {
      return customValidators.string().ValidateDriversLicense('Invalid format for selected state.', driverLicenceState[0]).required("Driver's License Number is required.");
    }),
    otherwise: (schema) => schema.notRequired()
  }),
  driverLicenseName: customValidators.string().when('validDriverLicense', {
    is: "true",
    then: (schema) => schema.isValidNameLimitedSpecialChar("Name on Driver's License is required.", true, false),
    otherwise: (schema) => schema.notRequired()
  }),
  includeSecondaryAdressee: string().when('state', {
    is: state => isSecondaryAddresseeState(state) === true,
    then: (schema) => schema.required('This field is required.'),
    otherwise: (schema) => schema.notRequired()
  }),
  secondaryAddresseeFirstName: customValidators.string().when(['state', 'includeSecondaryAdressee'], ([state, includeSecondaryAdressee], schema) => {
    return isSecondaryAddresseeState(state) === true && includeSecondaryAdressee === "true"
      ? schema.isValidNameLimitedSpecialChar('First Name is required.', true, false)
      : string().notRequired();
  }),
  secondaryAddresseeMiddleName: string()
    .matches(/^$|^(?!\s*$)[ a-zA-ZÀ-ÖØ-öø-ÿ'.-]{1}$/, 'Must be single alpha character.')
    .notRequired(),
  secondaryAddresseeLastName: customValidators.string().when(['state', 'includeSecondaryAdressee'], ([state, includeSecondaryAdressee], schema) => {
    return isSecondaryAddresseeState(state) === true && includeSecondaryAdressee === "true"
      ? schema.isValidNameLimitedSpecialChar('Last Name is required.', true, false)
      : string().notRequired();
  }),
  secondaryAddresseePhoneNumberType: string().when(['state', 'includeSecondaryAdressee'], ([state, includeSecondaryAdressee], schema) => {
    return isSecondaryAddresseeState(state) === true && includeSecondaryAdressee === "true"
      ? schema.required('Phone Number Type is required.')
      : string().notRequired();
  }),
  secondaryAddresseePhoneNumber: customValidators.string().when(['state', 'includeSecondaryAdressee'], ([state, includeSecondaryAdressee], schema) => {
    return isSecondaryAddresseeState(state) === true && includeSecondaryAdressee === "true"
      ? schema.required('Phone Number is required.')
        .isValidPhone('Phone Number must be formatted as "(123) 456-7890".')
      : string().notRequired();
  }),
  secondaryAddresseeAddress: customValidators.string().when(['state', 'includeSecondaryAdressee'], ([state, includeSecondaryAdressee], schema) => {
    return isSecondaryAddresseeState(state) === true && includeSecondaryAdressee === "true"
      ? schema.isValidAddress('Invalid address. No special character is allowed')
        .required('Address is required.')
      : string().notRequired();
  }),

  secondaryAddresseeCity: string().when(['state', 'includeSecondaryAdressee'], ([state, includeSecondaryAdressee], schema) => {
    return isSecondaryAddresseeState(state) === true && includeSecondaryAdressee === "true"
      ? schema.required('City is required.')
      : string().notRequired();
  }),

  secondaryAddresseeState: string().when(['state', 'includeSecondaryAdressee'], ([state, includeSecondaryAdressee], schema) => {
    return isSecondaryAddresseeState(state) === true && includeSecondaryAdressee === "true"
      ? schema.required('State is required.')
      : string().notRequired();
  }),

  secondaryAddresseeZip: customValidators.string().when(['state', 'includeSecondaryAdressee'], ([state, includeSecondaryAdressee], schema) => {
    return isSecondaryAddresseeState(state) === true && includeSecondaryAdressee === "true"
      ? schema.required()
        .isValidNumbersOnly('Invalid zip.', 5)
      : string().notRequired();
  }),
});

IdentityVerification.DefaultFormState = (prefill = false, basicInfo = null, analysisInfo = null) => {
  return {
    employmentStatus: prefill ? "Employed" : "",
    annualSalary: prefill ? "51525" : (basicInfo && basicInfo.income) || (analysisInfo && analysisInfo.annualIncome) || "",
    occupation: prefill ? "1" : "",
    validDriverLicense: prefill ? "false" : "",
    noDrivingReason: prefill ? "I use public or commercial transportation" : "",
    driverLicenseName: prefill ? "Proposed N Sured" : "",
    driverLicenceState: prefill ? "MA" : "",
    driverLicenseNumber: prefill ? "123a4567b" : "",
    includeSecondaryAdressee: prefill ? "false" : "",
    secondaryAddresseeFirstName: prefill ? "Proposed" : "",
    secondaryAddresseeMiddleName: prefill ? "N" : "",
    secondaryAddresseeLastName: prefill ? "Sured" : "",
    secondaryAddresseePhoneNumberType: prefill ? "home" : "",
    secondaryAddresseePhoneNumber: prefill ? "(123) 345-4567" : "",
    secondaryAddresseeAddress: prefill ? "123 Main Street" : "",
    secondaryAddresseeCity: prefill ? "Boston" : "",
    secondaryAddresseeState: prefill ? "MA" : "",
    secondaryAddresseeZip: prefill ? "02110" : "",
  };
};

IdentityVerification.propTypes = {
  formState: PropTypes.object.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  register: PropTypes.func,
  setValue: PropTypes.func,
  trigger: PropTypes.func,
  basicInfo: PropTypes.object,
};
